import * as React from "react"
import {useMemo, useEffect, useRef, useState} from "react"
import {Box, Button} from "@mui/material"
import {AutocompleteAsyncDebounced} from "@components"

import {BACKEND_APIS, CUSTOMER_REQUIRED_TYPES, PUIG_FULL_SCOPE_BRAND_IDS, PUIG_FULL_SCOPE_CUSTOMER_ID, RAW2SKU, USER_ENV} from "@constants"
import {useUserApp} from "@hooks"
import {createBatchFormData} from "../../@signals"

const extractId = (item) => item?.value ?? item


export default function BrandsAutocomplete({...props}) {
	const customerID = createBatchFormData.value.customerID
	const batchType = createBatchFormData.value.batchType
	const {userEnv} = useUserApp()
	// Only the customer-scoped directions gate this field: raw2sku has no customer
	// anywhere in its data path (`_create_raw2sku_batch` never sends `id_customer`
	// to ia-normalizer, and the drawer does not even render the picker), so waiting
	// for one left Brands permanently disabled there — while the back has always
	// accepted `id_brands` for it, both plain and split by brand.
	const customerScoped = CUSTOMER_REQUIRED_TYPES.includes(batchType)
	const waitingForCustomer = customerScoped && !customerID
	// Bumped to remount the picker, which is what makes it reload its options —
	// see handleSelectFullScope.
	const [pickerKey, setPickerKey] = useState(0)
	const previousCustomer = useRef(extractId(customerID))

	// `userEnv` is the deployment (backend Config.ENV) and is always required:
	// the scope list is Puig's. On the customer-scoped directions the customer is
	// checked too, because customer 2 of retail is a different company from
	// customer 2 of puig and neither check covers the other. raw2sku has no
	// customer to check, so there the deployment is the whole gate. Hidden rather
	// than disabled, so nobody is invited to press it where it does not apply
	// (Daniel, 2026-09-17).
	const showFullScope = userEnv === USER_ENV.PUIG
		&& (batchType === RAW2SKU
			|| Number(extractId(customerID)) === PUIG_FULL_SCOPE_CUSTOMER_ID)

	// Build filters for brand search
	const filters = useMemo(() => {
		const f = {}
		if (customerID) f.customerID = customerID
		return f
	}, [customerID])

	// Reset brands when the customer actually changes. Keyed on the id and guarded
	// by a ref: the effect also runs on mount, and the full-scope button remounts
	// this component right after filling `brands`, so an unguarded reset would wipe
	// the 65 it had just selected.
	useEffect(() => {
		const current = extractId(customerID)
		if (previousCustomer.current !== current && createBatchFormData.value.brands?.length > 0) {
			createBatchFormData.value = {...createBatchFormData.value, brands: []}
		}
		previousCustomer.current = current
	}, [extractId(customerID)])

	// The picker stores plain ids, never option objects: `AutocompleteSeeker`
	// renders `options.filter(op => value.includes(op.value))`, so an object never
	// matches and the field comes out empty, and `AutocompleteAjaxSeeker` trims the
	// value to whatever the last load returned — dropping everything that is not in
	// it. What brings the 65 back into those options is the `selected` param the
	// seeker already sends on every load, which `BrandOptionsServices` sorts to the
	// front: it only travels correctly as ids. Remounting forces that load now, so
	// the chips get their labels instead of waiting for the next keystroke.
	const handleSelectFullScope = () => {
		createBatchFormData.value = {...createBatchFormData.value, brands: [...PUIG_FULL_SCOPE_BRAND_IDS]}
		setPickerKey(key => key + 1)
	}

	return <>
		<AutocompleteAsyncDebounced
			key={pickerKey}
			multiple
			urlAjax={`${BACKEND_APIS.BRANDS}/crud/brand/find-options`}
			label="Brands"
			placeholder={waitingForCustomer ? "Select a Customer first" : "Search Brands..."}
			value={createBatchFormData.value.brands}
			onChange={e => createBatchFormData.value = {...createBatchFormData.value, brands: e.target.value}}
			filters={filters}
			{...props}
			// After the spread on purpose: the parent passes `disabled={isCreating}`,
			// which used to override the customer check and leave the field usable
			// under a placeholder that said otherwise.
			disabled={props.disabled || waitingForCustomer}
		/>

		{ showFullScope && <Box sx={{ mt: 1 }}>
			<Button
				size="small"
				variant="outlined"
				onClick={handleSelectFullScope}
				disabled={props.disabled}
			>
				{`Select full scope (${PUIG_FULL_SCOPE_BRAND_IDS.length} brands)`}
			</Button>
		</Box> }
	</>
}
